import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sync, Check, Info } from '@material-ui/icons';
import Grid from '@material-ui/core/Grid';
import { format as formatarData, isValid, parseISO } from 'date-fns';
import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/toast';
import DateComponent from '../../../../../../components/Inputs/Date';
import ModalProgresso, { FaseExecucao } from './ModalProgresso';
import {
  Container,
  StatusRodando,
  StatusParada,
  StatusConsultando,
  StatusRow,
  Checkpoint,
  Secao,
  SecaoHeader,
  Contador,
  Rodape,
  BotaoSincronizar,
  ListaEntidades,
  Entidade,
  Descricao,
  LinksSelecao,
  Aviso,
} from './styles';

interface SincronizacaoEntidadesProps {
  accountId: string | number;
}

interface IEntidade {
  chave: string;
  nome: string;
}

// Com o modal aberto o operador está olhando a execução: começa rápido, onde as
// transições de fase acontecem, e vai afrouxando. Fechado, o painel só precisa
// perceber que algo começou.
const CADENCIA_MODAL = [
  { consultas: 5, intervalo: 1000 },
  { consultas: 5, intervalo: 2000 },
];
const INTERVALO_MODAL_FINAL = 3000;
const INTERVALO_PAINEL = 10000;

// Tempo que o estado final fica visível antes do modal se fechar sozinho: o
// suficiente para ler o resultado, sem exigir uma ação para sair dele.
const ESPERA_PARA_FECHAR = 3000;

/**
 * Intervalo até a próxima consulta, a partir de quantas já foram feitas desde que
 * o modal abriu.
 */
const intervaloDoModal = (consultasFeitas: number): number => {
  let restantes = consultasFeitas;

  for (let i = 0; i < CADENCIA_MODAL.length; i += 1) {
    if (restantes < CADENCIA_MODAL[i].consultas) {
      return CADENCIA_MODAL[i].intervalo;
    }

    restantes -= CADENCIA_MODAL[i].consultas;
  }

  return INTERVALO_MODAL_FINAL;
};

// Tempo que a fase "na fila" espera por evidência de execução antes de se dar por
// encerrada: uma sincronização curta pode subir e descer entre duas consultas.
const ESPERA_MAXIMA_NA_FILA = 20000;

/**
 * A API devolve o timestamp cru do banco ("2026-07-30 14:22:10"), que não é ISO
 * válido por causa do espaço — daí a troca pelo T antes de interpretar. Valor não
 * reconhecido é exibido como veio, em vez de virar "Invalid Date".
 */
const formatarCheckpoint = (valor: string): string => {
  const data = parseISO(valor.replace(' ', 'T'));

  return isValid(data) ? formatarData(data, 'dd/MM/yyyy HH:mm:ss') : valor;
};

const SincronizacaoEntidades: React.FC<SincronizacaoEntidadesProps> = ({
  accountId,
}) => {
  const { addToast } = useToast();

  const [disponiveis, setDisponiveis] = useState<IEntidade[]>([]);
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [desde, setDesde] = useState<Date | null>(null);

  const [sincronizando, setSincronizando] = useState(false);
  const [ultimaSincronizacao, setUltimaSincronizacao] = useState<string | null>(
    null,
  );
  const [tipoSincronizacao, setTipoSincronizacao] = useState<string | null>(
    null,
  );
  const [enviando, setEnviando] = useState(false);

  // Fases de uma execução disparada aqui: fila (local) -> em andamento
  // (sincronizando = true) -> finalizada (voltou a false).
  const [fase, setFase] = useState<FaseExecucao | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [execucao, setExecucao] = useState<{
    entidades: string[];
    total: boolean;
    desde: string;
  } | null>(null);

  // Sem isso o painel monta com os defaults (parada / lista vazia) e só depois
  // recebe o estado real, o que aparece como uma piscada ao abrir a aba.
  const [carregandoStatus, setCarregandoStatus] = useState(true);
  const [carregandoEntidades, setCarregandoEntidades] = useState(true);

  // Prova visível de que o painel está recebendo resposta: sem isso, uma conta que
  // não muda de estado é indistinguível de um painel parado.
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);

  // Lidos dentro da consulta, que não deve ser recriada a cada mudança de fase —
  // recriá-la reiniciaria o ciclo de polling.
  const faseAtual = useRef<FaseExecucao | null>(null);
  const observadaEmExecucao = useRef(false);
  const enfileiradaEm = useRef(0);

  useEffect(() => {
    api
      .get<IEntidade[]>('/admin/beersales/entidades-sincronizacao')
      // A API devolve na ordem de execução (dependências entre entidades); aqui a
      // ordem é alfabética porque o backend reordena o que for enviado.
      .then(({ data }) =>
        setDisponiveis(
          [...data].sort((atual, proxima) =>
            atual.nome.localeCompare(proxima.nome, 'pt-BR'),
          ),
        ),
      )
      .catch(() =>
        addToast({
          type: 'error',
          title: 'Não foi possível carregar as entidades',
        }),
      )
      .finally(() => setCarregandoEntidades(false));
  }, [addToast]);

  const consultarStatus = useCallback(async () => {
    try {
      const { data } = await api.get<{
        sincronizando?: boolean;
        last_sync_at?: string | null;
        tipo_sincronizacao?: string | null;
      }>(`/admin/beersales/status-sincronizacao/${accountId}`);

      const rodando = !!data.sincronizando;

      if (rodando) {
        observadaEmExecucao.current = true;
      }

      if (faseAtual.current === 'enfileirada' && rodando) {
        faseAtual.current = 'executando';
        setFase('executando');
      } else if (faseAtual.current === 'executando' && !rodando) {
        faseAtual.current = 'finalizada';
        setFase('finalizada');
      } else if (
        faseAtual.current === 'enfileirada' &&
        !rodando &&
        Date.now() - enfileiradaEm.current > ESPERA_MAXIMA_NA_FILA
      ) {
        // Nunca vimos a conta em execução. Encerrar é melhor que deixar "na fila"
        // para sempre; o modal deixa claro que a execução não foi acompanhada.
        faseAtual.current = 'finalizada';
        setFase('finalizada');
      }

      setSincronizando(rodando);
      setUltimaSincronizacao(data.last_sync_at ?? null);
      setTipoSincronizacao(data.tipo_sincronizacao ?? null);
      setAtualizadoEm(new Date());
    } catch (error) {
      // Falha de consulta não interrompe o painel; a próxima tentativa reavalia.
    } finally {
      setCarregandoStatus(false);
    }
  }, [accountId]);

  useEffect(() => {
    consultarStatus();
  }, [consultarStatus]);

  const nuncaSincronizada = !carregandoStatus && !ultimaSincronizacao;

  // Conta sem checkpoint só admite sincronização total, então a seleção já vem
  // completa e travada — escolher entidades ali não é uma opção válida.
  useEffect(() => {
    if (nuncaSincronizada && disponiveis.length) {
      setSelecionadas(disponiveis.map(entidade => entidade.chave));
    }
  }, [nuncaSincronizada, disponiveis]);

  // Consulta enquanto o painel estiver aberto, e não apenas durante uma execução:
  // a sincronização automática (ou outro operador) pode começar a qualquer momento,
  // e o painel precisa refletir isso sozinho.
  // setTimeout em cadeia, e não setInterval: o intervalo muda conforme a cadência
  // avança, e assim a próxima consulta só é agendada depois que a anterior
  // respondeu — sem requisições se acumulando quando a API está lenta.
  // O contador zera quando o modal abre ou fecha, porque o efeito é recriado.
  useEffect(() => {
    let cancelado = false;
    let temporizador: ReturnType<typeof setTimeout>;
    let consultasFeitas = 0;

    const agendar = () => {
      const intervalo = modalAberto
        ? intervaloDoModal(consultasFeitas)
        : INTERVALO_PAINEL;

      temporizador = setTimeout(async () => {
        if (cancelado) return;

        // Mesma proteção do useSyncBeersales: aba em segundo plano não consulta.
        // Também não conta como consulta, para não gastar a cadência rápida.
        if (document.visibilityState === 'visible') {
          consultasFeitas += 1;
          await consultarStatus();
        }

        if (!cancelado) {
          agendar();
        }
      }, intervalo);
    };

    agendar();

    // Ao voltar para a aba, atualiza na hora em vez de esperar o próximo ciclo.
    const consultarAoVoltar = () => {
      if (document.visibilityState === 'visible') {
        consultarStatus();
      }
    };

    document.addEventListener('visibilitychange', consultarAoVoltar);

    return () => {
      cancelado = true;
      clearTimeout(temporizador);
      document.removeEventListener('visibilitychange', consultarAoVoltar);
    };
  }, [modalAberto, consultarStatus]);

  // Fecha o modal um instante depois de concluir. O timer é limpo se o operador
  // fechar antes ou se outra execução for disparada.
  useEffect(() => {
    if (!modalAberto || fase !== 'finalizada') return undefined;

    const temporizador = setTimeout(
      () => setModalAberto(false),
      ESPERA_PARA_FECHAR,
    );

    return () => clearTimeout(temporizador);
  }, [modalAberto, fase]);

  const alternarEntidade = useCallback((entidade: string) => {
    setSelecionadas(atuais =>
      atuais.includes(entidade)
        ? atuais.filter(item => item !== entidade)
        : [...atuais, entidade],
    );
  }, []);

  const sincronizar = useCallback(async () => {
    if (!selecionadas.length) {
      addToast({
        type: 'error',
        title: 'Selecione ao menos uma entidade',
      });
      return;
    }

    // Conta nunca sincronizada é a exceção: ali o backend força o histórico
    // completo e ignora qualquer data informada.
    if (!desde && !nuncaSincronizada) {
      addToast({
        type: 'error',
        title: 'Informe a data inicial',
        description: 'A sincronização precisa de um ponto de partida.',
      });
      return;
    }

    setEnviando(true);

    try {
      await api.post(`/admin/beersales/sincronizar/${accountId}`, {
        entidades: selecionadas,
        desde: desde ? formatarData(desde, 'yyyy-MM-dd') : null,
      });

      setExecucao({
        entidades: selecionadas.map(
          chave =>
            disponiveis.find(entidade => entidade.chave === chave)?.nome ??
            chave,
        ),
        total: selecionadas.length === disponiveis.length,
        desde: desde ? formatarData(desde, 'dd/MM/yyyy') : 'todo o histórico',
      });

      observadaEmExecucao.current = false;
      enfileiradaEm.current = Date.now();
      faseAtual.current = 'enfileirada';
      setFase('enfileirada');
      setModalAberto(true);
    } catch (error) {
      const resposta = (
        error as {
          response?: { status?: number; data?: { message?: string } };
        }
      )?.response;

      // 409: a conta travou para outra sincronização que já estava em andamento.
      if (resposta?.status === 409) {
        setSincronizando(true);
      }

      addToast({
        type: 'error',
        title:
          resposta?.status === 409
            ? 'A conta já está sincronizando'
            : 'Não foi possível sincronizar',
        description: resposta?.data?.message,
      });
    } finally {
      setEnviando(false);
    }
  }, [
    accountId,
    addToast,
    desde,
    disponiveis,
    nuncaSincronizada,
    selecionadas,
  ]);

  const bloqueado = enviando || sincronizando || fase === 'enfileirada';
  const sincronizacaoTotal =
    disponiveis.length > 0 && selecionadas.length === disponiveis.length;

  if (carregandoStatus || carregandoEntidades) {
    return (
      <Container>
        <StatusRow>
          <StatusConsultando>Consultando status da conta...</StatusConsultando>
        </StatusRow>
      </Container>
    );
  }

  // Compara com o valor já salvo (vindo da API), não com o do formulário: trocar o
  // select sem salvar não habilita a sincronização no backend.
  if (tipoSincronizacao && tipoSincronizacao !== 'api_beersales') {
    return (
      <Container>
        <Descricao>
          Esta conta está configurada como
          <strong> {tipoSincronizacao}</strong>. A sincronização por entidade
          atende somente contas que conectam via API BeerSales.
        </Descricao>
      </Container>
    );
  }

  return (
    <Container>
      <StatusRow>
        {sincronizando || fase === 'enfileirada' ? (
          <StatusRodando>
            {sincronizando
              ? 'Sincronizando agora'
              : 'Na fila, aguardando início'}
          </StatusRodando>
        ) : (
          <StatusParada>Nenhuma sincronização em andamento</StatusParada>
        )}

        <Checkpoint>
          {ultimaSincronizacao ? (
            <>
              Último checkpoint automático:{' '}
              <strong>{formatarCheckpoint(ultimaSincronizacao)}</strong>
            </>
          ) : (
            'Conta nunca sincronizada'
          )}
          {atualizadoEm && (
            <>
              {' · '}
              consultado às {formatarData(atualizadoEm, 'HH:mm:ss')}
            </>
          )}
        </Checkpoint>
      </StatusRow>

      {nuncaSincronizada && (
        <Aviso>
          <Info />
          <span>
            Esta conta nunca sincronizou. A primeira execução precisa ser total
            — todo o histórico, de todas as entidades — porque não há checkpoint
            anterior para completar o que ficasse de fora. A seleção e o período
            só ficam disponíveis depois dela.
          </span>
        </Aviso>
      )}

      {!nuncaSincronizada && sincronizacaoTotal && (
        <Aviso>
          <Info />
          <span>
            Com todas as entidades marcadas, esta é uma sincronização total: ao
            concluir, o checkpoint da sincronização automática é avançado. Uma
            seleção parcial mantém o checkpoint atual.
          </span>
        </Aviso>
      )}

      <Secao>
        <SecaoHeader>
          <h2>Período</h2>
        </SecaoHeader>

        <Descricao>
          {nuncaSincronizada
            ? 'Todo o histórico será reprocessado.'
            : 'Reprocessa as entidades escolhidas desde a data informada. Numa seleção parcial, o checkpoint da sincronização automática não é alterado.'}
        </Descricao>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={4} lg={3}>
            <DateComponent
              id="desde"
              name="desde"
              label={
                nuncaSincronizada
                  ? 'Alterados a partir de'
                  : 'Alterados a partir de*'
              }
              format="dd/MM/yyyy"
              value={desde}
              setValue={setDesde}
              disabled={nuncaSincronizada}
            />
          </Grid>
        </Grid>
      </Secao>

      <Secao>
        <SecaoHeader>
          <h2>
            Entidades{' '}
            <Contador>
              ({selecionadas.length} de {disponiveis.length} selecionadas)
            </Contador>
          </h2>

          {!nuncaSincronizada && (
            <LinksSelecao>
              <button
                type="button"
                onClick={() =>
                  setSelecionadas(disponiveis.map(entidade => entidade.chave))
                }
              >
                Marcar todas
              </button>
              <button type="button" onClick={() => setSelecionadas([])}>
                Limpar
              </button>
            </LinksSelecao>
          )}
        </SecaoHeader>

        <ListaEntidades travada={nuncaSincronizada}>
          {disponiveis.map(entidade => {
            const selecionada = selecionadas.includes(entidade.chave);

            return (
              <Entidade
                key={entidade.chave}
                htmlFor={`entidade-${entidade.chave}`}
                selecionada={selecionada}
                title={entidade.chave}
              >
                <input
                  id={`entidade-${entidade.chave}`}
                  name={`entidade-${entidade.chave}`}
                  type="checkbox"
                  value={entidade.chave}
                  checked={selecionada}
                  disabled={nuncaSincronizada}
                  onChange={() => alternarEntidade(entidade.chave)}
                />
                <Check />
                <span>{entidade.nome}</span>
              </Entidade>
            );
          })}
        </ListaEntidades>
      </Secao>

      <Rodape>
        <BotaoSincronizar
          titulo="Sincronizar entidades"
          mensagem={
            sincronizacaoTotal
              ? 'Confirma a sincronização total?\nAo concluir, o checkpoint da sincronização automática será avançado.\nA conta fica travada para novas sincronizações até o término.'
              : `Confirma a sincronização de ${selecionadas.length} entidade(s)?\nO checkpoint da sincronização automática será mantido.\nA conta fica travada para novas sincronizações até o término.`
          }
          funcao={sincronizar}
          disabled={bloqueado}
        >
          <Sync style={{ marginRight: '8px' }} />
          {(() => {
            if (bloqueado) return 'Aguarde...';

            return sincronizacaoTotal
              ? 'Sincronizar tudo'
              : 'Sincronizar selecionadas';
          })()}
        </BotaoSincronizar>
      </Rodape>

      {fase && execucao && (
        <ModalProgresso
          aberto={modalAberto}
          fase={fase}
          entidades={execucao.entidades}
          total={execucao.total}
          desde={execucao.desde}
          observadaEmExecucao={observadaEmExecucao.current}
          onFechar={() => setModalAberto(false)}
        />
      )}
    </Container>
  );
};

export default SincronizacaoEntidades;
